import {colorEqual, GlobalRenderConstants, SceneNodes} from "@cm/template-nodes"
import {mathIsEqual, setThreeObjectPart, ThreeObject, updateTransform} from "@template-editor/helpers/three-object"
import {RectAreaLightTexturesLib, Three as THREE} from "@cm/material-nodes/three"
import {DisplayMode, ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {ThreeStandHelper} from "@template-editor/helpers/three-stand-helper"
import {anyDifference, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {configThreeHelperObjectLayers, HELPER_OBJECTS_LAYER, MIN_GIZMO_RADIUS, OVERLAY_OBJECTS_LAYER} from "@template-editor/helpers/helper-objects"
import {clampColor01} from "@template-editor/helpers/three-utils"

THREE.RectAreaLightNode.setLTC(RectAreaLightTexturesLib.init())

export class ThreeAreaLight extends ThreeObject<SceneNodes.AreaLight> {
    protected override renderObject: THREE.Group = new THREE.Group()
    private threeLight: THREE.RectAreaLight = new THREE.RectAreaLight(0xffffff, 1, 1, 1)
    private areaLightHelper: ThreeAreaLightHelper

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)
        this.areaLightHelper = new ThreeAreaLightHelper(this, threeSceneManagerService, onAsyncUpdate)

        this.renderObject.add(this.threeLight)
        this.renderObject.add(this.areaLightHelper.getRenderObject())

        this.onDisplayModeChange(threeSceneManagerService.$displayMode())
    }

    override onDisplayModeChange(displayMode: DisplayMode) {
        this.areaLightHelper.getRenderObject().visible = displayMode === "editor"
    }

    override setup(sceneNode: SceneNodes.AreaLight) {
        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["on", "color", "intensity", "width", "height"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return colorEqual(valueA, valueB)
                    return valueA === valueB
                },
                ({on, color, intensity, width, height}) => {
                    this.threeLight.visible = on
                    this.threeLight.color.setRGB(...color)
                    this.threeLight.intensity = GlobalRenderConstants.lightIntensityScale * intensity
                    this.threeLight.width = width
                    this.threeLight.height = height
                    this.threeLight.castShadow = true
                },
            ),

            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.threeLight)
                },
            ),

            this.areaLightHelper.update(sceneNode),
        ])
    }

    override dispose() {
        this.areaLightHelper.dispose()
        this.threeLight.dispose()
    }

    override onSelectionChange(selected: boolean) {
        this.areaLightHelper.onSelectionChange(selected)
    }
}

class ThreeAreaLightHelper extends ThreeObject<SceneNodes.AreaLight> {
    protected override renderObject: THREE.Group = new THREE.Group()
    private localMesh = new THREE.Group()
    private globalMesh = new THREE.Group()
    private targetMesh = new THREE.Group()

    private proxyLine: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>
    private proxyFrontMesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>
    private proxyBackMesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>
    private proxyLaser: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>
    private proxyRays: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>
    private proxyTarget: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>
    private proxyOccludedTarget: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>

    private standHelper: ThreeStandHelper

    private numRays = 5

    constructor(areaLight: ThreeAreaLight, threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)

        {
            const geometry = new THREE.BufferGeometry()
            geometry.setAttribute("position", new THREE.Float32BufferAttribute([0.5, 0.5, 0, -0.5, 0.5, 0, -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0], 3))

            const material = new THREE.LineBasicMaterial({color: new THREE.Color(0.73, 0.73, 0.73), fog: false})
            this.proxyLine = new THREE.Line(geometry, material)
        }
        this.proxyFrontMesh = new THREE.Mesh(new THREE.PlaneGeometry(), new THREE.MeshBasicMaterial({side: THREE.BackSide, fog: false}))
        this.proxyBackMesh = new THREE.Mesh(new THREE.PlaneGeometry(), new THREE.MeshBasicMaterial({side: THREE.FrontSide, color: 0x080808, fog: false}))
        {
            const geometry = new THREE.BufferGeometry()
            geometry.setAttribute("position", new THREE.Float32BufferAttribute(2 * 3 * this.numRays * this.numRays, 3))

            const material = new THREE.LineBasicMaterial({color: 0x666666, fog: false})
            this.proxyRays = new THREE.LineSegments(geometry, material)
        }

        this.localMesh.add(this.proxyLine)
        setThreeObjectPart(this.proxyLine, areaLight)
        this.localMesh.add(this.proxyFrontMesh)
        setThreeObjectPart(this.proxyFrontMesh, areaLight)
        this.localMesh.add(this.proxyBackMesh)
        setThreeObjectPart(this.proxyBackMesh, areaLight)
        this.localMesh.add(this.proxyRays)

        {
            const geometry = new THREE.BufferGeometry()
            geometry.setAttribute("position", new THREE.Float32BufferAttribute(6, 3))
            this.proxyLaser = new THREE.Line(geometry, new THREE.LineBasicMaterial({color: 0xee8888, fog: false}))
        }
        this.proxyTarget = new THREE.Mesh(new THREE.BoxGeometry(5, 5, 5), new THREE.MeshStandardMaterial({color: 0xff0000, fog: false}))

        this.proxyOccludedTarget = new THREE.Mesh(
            new THREE.BoxGeometry(5, 5, 5),
            new THREE.MeshStandardMaterial({color: 0xff0000, fog: false, transparent: true, opacity: 0.2, depthTest: false, depthWrite: false}),
        )
        this.standHelper = new ThreeStandHelper(threeSceneManagerService, onAsyncUpdate)

        this.globalMesh.add(this.proxyLaser)
        this.globalMesh.add(this.targetMesh)
        this.targetMesh.add(this.proxyTarget)
        this.targetMesh.add(this.proxyOccludedTarget)
        setThreeObjectPart(this.targetMesh, areaLight, "target")
        this.globalMesh.add(this.standHelper.getRenderObject())
        setThreeObjectPart(this.standHelper.getRenderObject(), areaLight)

        this.renderObject.add(this.localMesh)
        this.renderObject.add(this.globalMesh)

        configThreeHelperObjectLayers(this.getRenderObject())
        configThreeHelperObjectLayers(this.proxyOccludedTarget, OVERLAY_OBJECTS_LAYER)
    }

    setup(sceneNode: SceneNodes.AreaLight) {
        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["color", "intensity"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return colorEqual(valueA, valueB)
                    return valueA === valueB
                },
                ({color, intensity}) => {
                    this.proxyFrontMesh.material.color.copy(
                        clampColor01(new THREE.Color(...color).multiplyScalar(intensity * GlobalRenderConstants.lightIntensityScale)),
                    )
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform", "target"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform, target}) => {
                    const {position} = updateTransform(transform, this.localMesh)

                    const transformMatrix = new THREE.Matrix4().setPosition(new THREE.Vector3(target.x, target.y, target.z))
                    updateTransform(transformMatrix, this.targetMesh)

                    const laserBuffer = this.proxyLaser.geometry.getAttribute("position") as THREE.Float32BufferAttribute
                    laserBuffer.setXYZ(0, position.x, position.y, position.z)
                    laserBuffer.setXYZ(1, target.x, target.y, target.z)
                    laserBuffer.needsUpdate = true
                },
            ),
            objectFieldsDifferent(sceneNode, this.parameters, ["width", "height"], undefined, ({width, height}) => {
                const meshScale = new THREE.Matrix4().scale(new THREE.Vector3(width, height, 1))
                updateTransform(meshScale, this.proxyFrontMesh)
                updateTransform(meshScale, this.proxyBackMesh)

                const lineWidth = Math.max(width, 2 * MIN_GIZMO_RADIUS)
                const lineHeight = Math.max(height, 2 * MIN_GIZMO_RADIUS)
                updateTransform(new THREE.Matrix4().scale(new THREE.Vector3(lineWidth, lineHeight, 1)), this.proxyLine)
            }),
            objectFieldsDifferent(sceneNode, this.parameters, ["targeted"], undefined, ({targeted}) => {
                this.proxyLaser.visible = targeted
                this.proxyTarget.visible = targeted
                this.proxyOccludedTarget.visible = targeted
            }),
            this.updateRays(sceneNode),
            this.standHelper.update(sceneNode),
            objectFieldsDifferent(sceneNode, this.parameters, ["targeted"], undefined, ({targeted}) => {
                this.proxyLaser.visible = targeted
                this.proxyTarget.visible = targeted
                this.proxyOccludedTarget.visible = targeted
            }),
            objectFieldsDifferent(sceneNode, this.parameters, ["transparent", "visibleDirectly"], undefined, ({transparent, visibleDirectly}) => {
                const switchMaterialTransparent = (material: THREE.Material, transparent: boolean) => {
                    material.transparent = transparent
                    material.opacity = transparent ? 0 : 1
                    material.depthWrite = !transparent
                    material.needsUpdate = true
                }

                switchMaterialTransparent(this.proxyBackMesh.material, transparent || !visibleDirectly)
                switchMaterialTransparent(this.proxyFrontMesh.material, !visibleDirectly)

                configThreeHelperObjectLayers(this.proxyBackMesh, this.proxyBackMesh.material.depthWrite ? HELPER_OBJECTS_LAYER : OVERLAY_OBJECTS_LAYER)
                configThreeHelperObjectLayers(this.proxyFrontMesh, this.proxyFrontMesh.material.depthWrite ? HELPER_OBJECTS_LAYER : OVERLAY_OBJECTS_LAYER)
            }),
        ])
    }

    private updateRays(sceneNode: SceneNodes.AreaLight) {
        return objectFieldsDifferent(
            sceneNode,
            this.parameters,
            ["width", "height", "directionality", "intensity"],
            undefined,
            ({width, height, directionality, intensity}) => {
                const bufferPositionRays = this.proxyRays.geometry.getAttribute("position") as THREE.Float32BufferAttribute

                const w = Math.max(width, 2 * MIN_GIZMO_RADIUS)
                const h = Math.max(height, 2 * MIN_GIZMO_RADIUS)

                const directionalityRad = (Math.min(1.0, Math.max(0.0, directionality)) * Math.PI) / 2.0
                const edgefxy = Math.cos(directionalityRad)
                const edgefz = Math.sin(directionalityRad)
                const centerfz = 1.0
                const centerRayIdx = this.numRays % 2 === 1.0 ? Math.floor(this.numRays / 2.0) : -1

                for (let ix = 0; ix < this.numRays; ix++) {
                    for (let iy = 0; iy < this.numRays; iy++) {
                        const tx = (ix / (this.numRays - 1) - 0.5) * w * 0.9
                        const ty = (iy / (this.numRays - 1) - 0.5) * h * 0.9

                        let fx = 0.0
                        let fy = 0.0
                        let fz = 1.0

                        if (!(iy === centerRayIdx && ix === centerRayIdx)) {
                            const norm = Math.sqrt(tx ** 2 + ty ** 2)
                            const scaling = (w / 2.0 / Math.abs(tx)) * Math.abs(ty) < h / 2.0 ? (2 * Math.abs(tx)) / w : (2.0 * Math.abs(ty)) / h
                            fx = (tx / norm) * scaling * edgefxy
                            fy = (ty / norm) * scaling * edgefxy
                            fz = scaling * edgefz + (1.0 - scaling) * centerfz
                        }

                        const fac = Math.sqrt(fx ** 2 + fy ** 2 + fz ** 2) / (2 * intensity)
                        bufferPositionRays.setXYZ(2 * (iy * this.numRays + ix), tx, ty, 0)
                        bufferPositionRays.setXYZ(2 * (iy * this.numRays + ix) + 1, tx + fx / fac, ty + fy / fac, -fz / fac)
                    }
                }

                bufferPositionRays.needsUpdate = true
            },
        )
    }

    override dispose() {
        this.proxyLine.geometry.dispose()
        this.proxyLine.material.dispose()
        this.proxyFrontMesh.geometry.dispose()
        this.proxyFrontMesh.material.dispose()
        this.proxyBackMesh.geometry.dispose()
        this.proxyBackMesh.material.dispose()
        this.proxyRays.geometry.dispose()
        this.proxyRays.material.dispose()

        this.proxyLaser.geometry.dispose()
        this.proxyLaser.material.dispose()
        this.proxyTarget.geometry.dispose()
        this.proxyTarget.material.dispose()
        this.proxyOccludedTarget.geometry.dispose()
        this.proxyOccludedTarget.material.dispose()
        this.standHelper.dispose()
    }

    override onSelectionChange(selected: boolean) {
        const targeted = this.parameters?.targeted ?? false

        this.proxyLaser.visible = selected && targeted
        this.proxyTarget.visible = selected && targeted
        this.proxyOccludedTarget.visible = selected && targeted
    }
}
